import { cva } from 'class-variance-authority';

export const organizationSearchWrapperVariants = cva('shrink-0 px-2 pt-3 pb-1.5');

export const organizationScrollAreaVariants = cva(
  'scrollbar-thin-stable min-h-0 flex-1 overflow-y-auto overscroll-contain px-1.5 pb-2 outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
);

export const organizationSectionLabelVariants = cva('px-1 pt-2 pb-1');

export const organizationListVariants = cva('relative transition-opacity', {
  variants: {
    searching: { true: 'opacity-60', false: null },
  },
  defaultVariants: { searching: false },
});

export const organizationTreeRowVariants = cva(
  'flex w-full items-center text-left outline-none transition-colors focus-visible:ring-0',
  {
    variants: {
      density: {
        comfortable: 'h-12 gap-3 rounded-xl px-2 hover:bg-muted active:bg-muted',
        compact:
          'h-7 gap-1 rounded-md px-0.5 hover:bg-sidebar-accent focus-visible:ring-1 focus-visible:ring-sidebar-ring',
      },
      active: { true: null, false: null },
    },
    compoundVariants: [
      { density: 'comfortable', active: true, className: 'bg-sidebar-accent font-medium' },
      { density: 'compact', active: true, className: 'bg-sidebar-accent' },
    ],
    defaultVariants: { density: 'compact', active: false },
  },
);

export const organizationTreeChevronBoxVariants = cva(
  'flex shrink-0 items-center justify-center text-muted-foreground',
  {
    variants: { density: { comfortable: 'size-8', compact: 'size-6' } },
    defaultVariants: { density: 'compact' },
  },
);

export const organizationTreeChevronIconVariants = cva(null, {
  variants: { density: { comfortable: 'size-5', compact: 'size-3.5' } },
  defaultVariants: { density: 'compact' },
});

export const organizationTreeMarkVariants = cva(
  'flex shrink-0 items-center justify-center border border-border bg-background font-semibold',
  {
    variants: {
      density: {
        comfortable: 'size-9 rounded-lg text-xs',
        compact: 'size-5 rounded text-[9px]',
      },
      active: {
        true: 'border-transparent bg-primary text-primary-foreground',
        false: 'text-muted-foreground',
      },
    },
    defaultVariants: { density: 'compact', active: false },
  },
);

export const organizationTreeNameVariants = cva('min-w-0 flex-1 truncate', {
  variants: {
    density: { comfortable: 'text-[15px] font-medium', compact: 'text-xs' },
    active: { true: 'font-medium text-sidebar-accent-foreground', false: null },
  },
  defaultVariants: { density: 'compact', active: false },
});

export const organizationTreeCountVariants = cva(
  'shrink-0 tabular-nums text-muted-foreground transition-opacity',
  {
    variants: {
      density: { comfortable: 'pr-0.5 text-xs', compact: 'pr-1 text-[11px]' },
      fade: {
        true: 'group-hover/org:opacity-0 group-focus-within/org:opacity-0',
        false: null,
      },
    },
    defaultVariants: { density: 'compact', fade: false },
  },
);

export const organizationProjectListVariants = cva(
  'ml-3.5 border-sidebar-border/80 border-l pl-2',
  {
    variants: {
      density: {
        comfortable: 'ml-6 border-border pl-3',
        compact: null,
      },
      edge: {
        first: 'pt-0.5',
        middle: null,
        last: 'mb-0.5 pb-0.5',
        only: 'mb-0.5 py-0.5',
      },
    },
    compoundVariants: [
      { density: 'comfortable', edge: 'last', className: 'mb-2 pb-2' },
      { density: 'comfortable', edge: 'only', className: 'mb-2 py-2' },
    ],
    defaultVariants: { density: 'compact', edge: 'middle' },
  },
);

export const organizationProjectLinkVariants = cva(
  'flex items-center outline-none transition-colors',
  {
    variants: {
      density: {
        comfortable:
          'h-12 gap-3 rounded-xl border border-border bg-card px-3.5 text-[15px] font-medium text-foreground hover:bg-muted active:bg-muted focus-visible:ring-0 data-[active=true]:border-foreground/20 data-[active=true]:bg-muted',
        compact:
          'h-6 gap-1.5 rounded-md px-1.5 text-xs text-muted-foreground ring-sidebar-ring hover:bg-sidebar-accent/70 hover:text-foreground focus-visible:ring-1 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground',
      },
    },
    defaultVariants: { density: 'compact' },
  },
);

export const organizationProjectIconVariants = cva('shrink-0', {
  variants: {
    density: { comfortable: 'size-4 text-muted-foreground', compact: 'size-3 opacity-70' },
  },
  defaultVariants: { density: 'compact' },
});

export const organizationProjectSkeletonVariants = cva('w-full', {
  variants: {
    density: { comfortable: 'h-12 rounded-xl', compact: 'my-0.5 h-6 rounded-md' },
  },
  defaultVariants: { density: 'compact' },
});

export const organizationProjectMessageVariants = cva(null, {
  variants: {
    tone: { muted: 'text-muted-foreground', destructive: 'text-destructive' },
    density: { comfortable: 'px-3 py-2 text-sm', compact: 'py-1 text-[11px]' },
  },
  compoundVariants: [
    { tone: 'muted', density: 'compact', className: 'px-1.5' },
    { tone: 'destructive', density: 'compact', className: 'px-2' },
  ],
  defaultVariants: { tone: 'muted', density: 'compact' },
});
